import * as React from 'react'
import { useCallback, useMemo, JSX } from 'react'

import { areArraysEqual } from '@/utils/areArraysEqual'
import { Filter } from '../../common/Filter/Filter'
import { CheckboxFilterDropdownContent } from '../../common/Filter/CheckboxFilter/CheckboxFilterDropdownContent'
import {
    useFiltersApi,
    useFiltersStore,
    useSearch,
    type Node,
} from '../../../hooks'
import { useTranslation } from 'react-i18next'
import { useFilterCallbacks } from '../../common/Filter/useFilterCallbacks'
import { flattenNodes } from '../../../utils/flattenNodes'
import { getCheckedNodeIds } from '../../../utils/getCheckedNodeIds'
import { NestedOption } from '../../data/AnalyticsFilters/types'
import { highlightMatchingText } from '../../../utils/highlightMatchingText'
import { createNodeTreeStateGenerator } from '../../../utils/createNodeTreeStateGenerator'
import { GetNodeWithState } from '../../../types'
import { useFilterLabels } from './useFilterLabels'
import { Option } from '../../common/Form/types'
import { FilterProps } from '../../common/Filter/types'
import { WidgetSpinner } from '@/components/common/Spinner/Spinner'

export type NodeFilterProps = {
    name: string
    onChange: (node: string[]) => void
    value: string[]
    defaultValue: string[]
    disabled?: boolean
    valueLabel?: string
    className?: string
} & Pick<FilterProps, 'containerRef'>

export const NodeFilter: React.FC<NodeFilterProps> = (props) => {
    const { disabled, value, defaultValue, name } = props

    const { useNodesQuery } = useFiltersApi()
    const { data, isLoading, isError, isFetched, isFetching } = useNodesQuery()
    const labels = useFilterLabels()

    const nodes: Node[] = useMemo(() => {
        return data?.nodes ?? []
    }, [data])
    const rootNode = useFiltersStore((state) => state.rootNode)

    const onChange = (value: string[]) => {
        const flatNodes = flattenNodes({
            nodes,
            getChildren: (node) => node.nodes,
        })
        const selectedNodes = flatNodes.filter((rawOption) =>
            value.includes(rawOption.nodeId.toString())
        )

        if (rootNode && selectedNodes.length === 0) {
            props.onChange([rootNode.nodeId.toString()])
            return
        }

        props.onChange(selectedNodes.map((node) => node.nodeId.toString()))
    }

    const nodeTreeGenerator = createNodeTreeStateGenerator({
        getNodeChildren: (node) => node.nodes,
    })

    const { t } = useTranslation('translations')

    const {
        currentValue,
        setCurrentValue,
        onValueSave,
        onValueReset,
        resetState,
    } = useFilterCallbacks(value, defaultValue, onChange, {
        compareValues: areArraysEqual,
        onValueSaveCb: () => {
            setSearchValue('')
        },
        onValueResetCb: () => {
            setSearchValue('')
        },
        onResetStateCb: () => {
            setSearchValue('')
        },
    })

    const renderRichLabel = useCallback(
        (
            option: Option,
            highlightMatchingText?: (input: string) => string | JSX.Element
        ) => {
            const flatNodes = flattenNodes({
                nodes,
                getChildren: (n) => n.nodes,
            })
            const rawOption = flatNodes.find(
                (rawOption) => rawOption.nodeId.toString() === option.value
            )

            if (rawOption) {
                return (
                    <div>
                        {highlightMatchingText?.(rawOption.nodeName) ??
                            rawOption.nodeName}
                    </div>
                )
            }

            return option.label
        },
        [nodes]
    )

    const nodesWithState = nodeTreeGenerator({
        nodes,
        selectedNodeIds: currentValue,
    })

    const flatNodesWithState = flattenNodes({
        nodes: nodesWithState,
        getChildren: (n) => n.nodes,
    })

    const mappedFlatNodesWithState = flatNodesWithState.map(mapOption)
    const mappedNodesWithState = nodesWithState.map(mapOption)

    React.useEffect(() => {
        setCurrentValue(value)
    }, [value, setCurrentValue])

    const {
        searchValue,
        setSearchValue,
        options: displayedOptions,
    } = useSearch((sValue) => {
        const options = sValue ? mappedFlatNodesWithState : mappedNodesWithState
        return {
            options: options,
        }
    })

    const valueLabel = useMemo(() => {
        if (isLoading || isFetching) {
            return <WidgetSpinner />
        }
        if (props.valueLabel) {
            return props.valueLabel
        }

        if (
            currentValue.length === 1 &&
            currentValue[0] === rootNode?.nodeId.toString()
        ) {
            return t('filters.common.all')
        }

        const optionsLength = mappedFlatNodesWithState.length
        const selectedOptionsLength = value.length

        if (
            selectedOptionsLength === 0 ||
            selectedOptionsLength === optionsLength
        ) {
            return t('filters.common.all')
        }

        if (selectedOptionsLength > 1) {
            return t('filters.common.multipleSelections', {
                selectedNumber: selectedOptionsLength,
            })
        }

        const selectedOption = mappedFlatNodesWithState.find((opt) =>
            value.find((v) => v === opt.value)
        )

        if (selectedOption) {
            return selectedOption.label
        }

        return value[0]
    }, [
        isLoading,
        isFetching,
        props.valueLabel,
        currentValue,
        rootNode?.nodeId,
        mappedFlatNodesWithState,
        value,
        t,
    ])

    const renderLabel = useMemo(() => {
        return (option: Option) => {
            return renderRichLabel(option, (input) => {
                return highlightMatchingText({
                    text: input,
                    matchText: searchValue,
                })
            })
        }
    }, [renderRichLabel, searchValue])

    return (
        <Filter
            name={name}
            valueLabel={valueLabel}
            onValueSave={onValueSave}
            onValueReset={onValueReset}
            resetState={resetState}
            showReset
            searchConfig={{
                name: 'node-filter-search',
                placeholder: t('filters.node.searchPlaceholder'),
                value: searchValue,
                onChange: setSearchValue,
            }}
            options={displayedOptions}
            label={t(`filters.node.label`)}
            {...labels}
            isLoading={isLoading || isFetching || !isFetched}
            isError={isError}
            disabled={disabled}
            containerRef={props.containerRef}
            className={props.className}
        >
            <CheckboxFilterDropdownContent
                disabled={props.disabled}
                name={props.name}
                options={displayedOptions}
                renderLabel={renderLabel}
                value={currentValue}
                onChange={(value) => {
                    if (value.every((val) => typeof val === 'string')) {
                        const latestNodeWithState = nodeTreeGenerator({
                            nodes,
                            selectedNodeIds: value,
                        })
                        const latestCheckedNodeIds = getCheckedNodeIds({
                            nodes: latestNodeWithState,
                            getNodeChildren: (node) => node.nodes,
                        })

                        setCurrentValue(latestCheckedNodeIds)
                    }
                }}
            />
        </Filter>
    )
}

type NodeWithState = GetNodeWithState<Node>

const mapOption = ((node: Node | NodeWithState): NestedOption => {
    if ('state' in node) {
        return {
            value: node.nodeId.toString(),
            label: node.nodeName,
            children: node.nodes.map(mapOption),
            indeterminate: node.state === 'indeterminate',
        }
    }

    return {
        value: node.nodeId.toString(),
        label: node.nodeName,
        children: node.nodes.map(mapOption),
        indeterminate: false,
    }
}) as (node: unknown) => NestedOption
